import { useEffect, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { useCoachNote } from '../coach/hooks';
import './livecoach.scss';

// Rotating status phrases while the note generates — the note endpoint has no granular progress, so this is
// cosmetic (the bar is indeterminate). Kept honest: no scoring language, since the report isn't scored.
const PHRASES = [
  'Reviewing the full transcript',
  'Assessing each focus area',
  'Noting where the signal was thin',
  'Writing your summary',
];

const ArrowIcon = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    {...p}
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const CheckIcon = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    {...p}
  >
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const SparkIcon = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    {...p}
  >
    <path d="M12 3l1.9 4.6L18.5 9.5 14 11.4 12 16l-2-4.6L5.5 9.5 10 7.6z" />
  </svg>
);

interface Props {
  interviewId: number | string | undefined;
  // The call has ended (the round is over + the assistant is on) — only then do we poll + show the strip.
  enabled: boolean;
  // Route to the Coach tab (parent flips the phase to 'coach').
  onOpenCoach: () => void;
}

/**
 * Docked at the bottom of the Live Coach pane. When the call ends we poll the coach note; the strip shows
 * "preparing" with an indeterminate bar, then flips to "ready" with a button that opens the Coach tab. The
 * live view above stays fully readable throughout.
 */
export function CoachHandoffStrip({
  interviewId,
  enabled,
  onOpenCoach,
}: Props) {
  const { data: note } = useCoachNote(interviewId, enabled);
  const status = note?.status;
  const ready = status === 'completed';
  const failed = status === 'failed';
  const done = ready || failed;

  const [phraseIdx, setPhraseIdx] = useState(0);
  useEffect(() => {
    if (done) return;
    const id = setInterval(
      () => setPhraseIdx((i) => (i + 1) % PHRASES.length),
      2600
    );
    return () => clearInterval(id);
  }, [done]);

  if (!enabled) return null;

  const title = ready
    ? 'Your coaching report is ready'
    : failed
      ? "Couldn't prepare the coaching report"
      : 'Interview ended — preparing your coaching report';
  const sub = ready
    ? 'Open it whenever you’re ready — the live recap stays here.'
    : failed
      ? 'Open the Coach tab to retry.'
      : PHRASES[phraseIdx];

  return (
    <Flex
      className={`livecoach-handoff${ready ? ' is-ready' : ''}${failed ? ' is-failed' : ''}`}
      align="center"
      gap="15px"
      px="22px"
      py="15px"
      flex="none"
      position="relative"
    >
      {/* aurora orb → check when ready */}
      <Box className="lc-horb" flex="none">
        <span className="lc-horb-ring" />
        <span className="lc-horb-core">
          {ready ? <CheckIcon /> : <SparkIcon />}
        </span>
      </Box>

      <Box flex={1} minW={0}>
        <KText
          size="sm-semi-bold"
          color="secondary-900"
          fontSize="14px"
          fontWeight={700}
          noOfLines={1}
        >
          {title}
        </KText>
        <Flex align="center" gap="7px" mt="2px">
          {!done && <span className="lc-horb-dot" />}
          <KText
            size="xs-regular"
            color="secondary-600"
            fontSize="12px"
            className={done ? undefined : 'livecoach-progress-text'}
            noOfLines={1}
          >
            {sub}
          </KText>
        </Flex>
      </Box>

      <Flex
        as="button"
        align="center"
        gap="8px"
        flex="none"
        px="18px"
        py="11px"
        borderRadius="11px"
        fontSize="13px"
        fontWeight={650}
        whiteSpace="nowrap"
        onClick={done ? onOpenCoach : undefined}
        cursor={done ? 'pointer' : 'default'}
        className={
          done ? 'livecoach-handoff-cta' : 'livecoach-handoff-cta is-wait'
        }
      >
        {done ? (
          <>
            {failed ? 'Open Coach' : 'Open coaching report'}
            <ArrowIcon />
          </>
        ) : (
          <>
            <span
              className="livecoach-spin"
              style={{ width: 13, height: 13 }}
            />
            Preparing…
          </>
        )}
      </Flex>

      {/* indeterminate progress line along the top edge (seam with the live view) */}
      {!done && <span className="livecoach-handoff-bar" />}
    </Flex>
  );
}

export default CoachHandoffStrip;
